import * as React from 'react'
import throttle from 'lodash/throttle'
import { useRefDep } from './ref-dep'
import 'intersection-observer'

/**
 * Gets the scroll offset on every scroll tick. Causes a rerender.
 * If animating, use the spring variant.
 *
 * @param scrollRef
 */
export function useScrollOffset(scrollRef: React.RefObject<Element | null>) {
  const [offset, setOffset] = React.useState(0)
  React.useEffect(() => {
    const $elem = scrollRef.current
    if (!$elem) {
      return
    }

    const handler = (e: Event) => setOffset((e.target as HTMLElement).scrollTop)
    $elem.addEventListener('scroll', handler)
    return () => {
      $elem.removeEventListener('scroll', handler)
    }
  }, [useRefDep(scrollRef)])
  return offset
}

/**
 * Hook that returns the viewport size and listens for changes.
 */
export function useViewportSize() {
  const [viewport, setViewport] = React.useState(() => readViewportSize())
  const updateViewport = React.useCallback(
    () => setViewport(readViewportSize()),
    [],
  )
  React.useEffect(() => {
    const handler = throttle(updateViewport, 400)
    window.addEventListener('resize', handler)
    return () => {
      window.removeEventListener('resize', handler)
    }
  }, [updateViewport])
  return viewport
}

/**
 * Hook that observes whether the element is in view.
 *
 * @param ref
 * @param callback
 */
export function useIsElementInView(
  ref: React.RefObject<Element | null>,
  callback?: (isInView: boolean) => unknown,
) {
  const [isInView, setIsInView] = React.useState(false)
  React.useEffect(() => {
    if (!ref.current) {
      return
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        setIsInView(entry.isIntersecting)
        callback?.(entry.isIntersecting)
      })
    })
    observer.observe(ref.current)
    return () => {
      observer.disconnect()
    }
  }, [useRefDep(ref), callback])
  return isInView
}

function readViewportSize() {
  const width = Math.max(
    document.documentElement.clientWidth,
    window.innerWidth || 0,
  )
  const height = Math.max(
    document.documentElement.clientHeight,
    window.innerHeight || 0,
  )
  return { width, height }
}
